import React from 'react';
import useFeatureAccess from 'app/services/feature-access';
import { Button, Card } from '@shadcn/ui';
import { LockIcon } from 'lucide-react';
import { useToast } from '@shadcn/hooks/use-toast';

interface FeatureGateProps {
  feature: string;
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
  children: React.ReactNode;
  currentCount?: number; // For features with usage limits
}

export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  fallback,
  showUpgradePrompt = true,
  children,
  currentCount,
}) => {
  const { hasAccess, hasReachedBuildingLimit, getBuildingLimit, currentPlan, accountStatus } = useFeatureAccess();
  const { toast } = useToast();

  // Check if the user has access to the feature
  const canAccess =
    feature === 'buildingsLimit' ? !hasReachedBuildingLimit(currentCount || 0) : hasAccess(feature as any);

  const featureNames: Record<string, string> = {
    uploadUtilityBills: 'Upload Utility Bills',
    energyStarIntegration: 'ENERGY STAR Integration',
    portfolioAnalysis: 'Portfolio-Level Analysis',
    complianceTracking: 'Compliance Tracking',
    exportEmissionsReports: 'Export Emissions Reports',
    realTimeMonitoring: 'Real-Time Monitoring',
    buildingsLimit: 'Add More Buildings',
    reportBuilder: 'Report Builder',
    financialInsights: 'Financial Insights',
    complianceManagement: 'Compliance Management',
  };

  const requiredPlanMap: Record<string, string> = {
    energyStarIntegration: 'Starter',
    portfolioAnalysis: 'Starter',
    complianceTracking: 'Professional',
    exportEmissionsReports: 'Business',
    realTimeMonitoring: 'Business',
    buildingsLimit: 'Professional',
    reportBuilder: 'Professional',
    financialInsights: 'Business',
    complianceManagement: 'Professional',
  };

  const handleUpgradeClick = () => {
    toast({
      description: 'Please upgrade your subscription to access this feature.',
      variant: 'default',
    });
    // You could navigate to the profile page or directly open the subscription modal
    window.location.href = '/account/profile';
  };

  // First check if account is inactive (for non-residential accounts)
  if (currentPlan !== 'residential' && accountStatus === 'inactive') {
    if (fallback) {
      return <>{fallback}</>;
    }

    if (showUpgradePrompt) {
      return (
        <Card className="flex flex-col items-center justify-center p-6 text-center">
          <LockIcon className="mb-3 h-12 w-12 text-muted-foreground/50" />
          <h3 className="mb-2 text-lg font-medium">Account Inactive</h3>
          <p className="mb-4 text-sm text-muted-foreground/80">
            Your account is currently inactive. Please visit your profile to reactivate your account.
          </p>
          <Button
            onClick={() => (window.location.href = '/account/profile')}
            className="bg-primary/90 text-primary-foreground hover:bg-primary"
          >
            Go to Profile
          </Button>
        </Card>
      );
    }

    return null;
  }

  // If the user has access, render the children
  if (canAccess) {
    return <>{children}</>;
  }

  // If fallback is provided, render it
  if (fallback) {
    return <>{fallback}</>;
  }

  // Otherwise render the upgrade prompt if showUpgradePrompt is true
  if (showUpgradePrompt) {
    const featureName = featureNames[feature] || feature;
    const requiredPlan = requiredPlanMap[feature] || 'higher';
    const limitMessage =
      feature === 'buildingsLimit'
        ? `You've reached the limit of ${getBuildingLimit()} buildings on your ${currentPlan} plan.`
        : `${featureName} is available on the ${requiredPlan} plan or higher.`;

    return (
      <Card className="flex flex-col items-center justify-center p-6 text-center">
        <LockIcon className="mb-3 h-12 w-12 text-muted-foreground/50" />
        <h3 className="mb-2 text-lg font-medium">Premium Feature</h3>
        <p className="mb-4 text-sm text-muted-foreground/80">{limitMessage}</p>
        <Button onClick={handleUpgradeClick} className="bg-primary/90 text-primary-foreground hover:bg-primary">
          Upgrade Plan
        </Button>
      </Card>
    );
  }

  // If no fallback and no upgrade prompt, render nothing
  return null;
};

export default FeatureGate;
